import React, { useState } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Badge, Drawer } from 'antd'
import {
  DashboardOutlined,
  ShoppingOutlined,
  ContactsOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  FileOutlined,
  BarChartOutlined,
  TeamOutlined,
  CloudUploadOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  MenuOutlined,
  BuildingOutlined,
  ShopOutlined,
  ApiOutlined,
  AppstoreOutlined,
  TagOutlined,
  BankOutlined,
  ImportOutlined
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import ProfileModal from '../Modals/ProfileModal'
import ChangePasswordModal from '../Modals/ChangePasswordModal'

const { Header, Sider, Content } = Layout

const DashboardLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [mobileDrawerVisible, setMobileDrawerVisible] = useState(false)
  const [profileModalVisible, setProfileModalVisible] = useState(false)
  const [passwordModalVisible, setPasswordModalVisible] = useState(false)

  const { user, logout, hasPermission, hasRole } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  // Menu items based on user permissions
  const getMenuItems = () => {


    const items = [
      {
        key: '/dashboard',
        icon: <DashboardOutlined />,
        label: 'แดชบอร์ด',
      }
    ]

    if (hasPermission('projects', 'read')) {
      items.push({
        key: '/projects',
        icon: <ContactsOutlined />,
        label: 'โปรเจ็ค',
      })
    }

    if (hasPermission('products', 'read')) {
      items.push({
        key: '/products',
        icon: <ShoppingOutlined />,
        label: 'สินค้า',
      })
    }

    if (hasPermission('contacts', 'read')) {
      items.push({
        key: '/contacts',
        icon: <ContactsOutlined />,
        label: 'รายชื่อลูกค้า/คู่ค้า',
      })
    }

    if (hasPermission('purchases', 'read')) {
      items.push({
        key: '/purchases',
        icon: <FileTextOutlined />,
        label: 'ใบสั่งซื้อ',
      })
    }

    if (hasPermission('quotes', 'read')) {
      items.push({
        key: '/quotes',
        icon: <FileTextOutlined />,
        label: 'ใบเสนอราคา',
      })
    }

    if (hasPermission('quotes', 'read')) {
      items.push({
        key: '/delivery-notes',
        icon: <FileTextOutlined />,
        label: 'ใบส่งของชั่วคราว',
      })
    }

    if (hasPermission('quotes', 'read')) {
      items.push({
        key: '/delivery-pending',
        icon: <FileTextOutlined />,
        label: 'ใบค้างส่ง',
      })
    }

    if (hasPermission('receipts', 'read')) {
      items.push({
        key: '/receipts',
        icon: <FileTextOutlined />,
        label: 'แจ้งหนี้/ใบเสร็จ',
      })
    }

    if (hasPermission('expenses', 'read')) {
      items.push({
        key: '/expenses',
        icon: <CreditCardOutlined />,
        label: 'ค่าใช้จ่าย',
      })
    }

    // Director Advance
    items.push({
      key: '/director-advance',
      icon: <BankOutlined />,
      label: 'เงินทดรองกรรมการ',
    })

    items.push({
      key: '/bank-statement-import',
      icon: <ImportOutlined />,
      label: 'รายการ Statement',
    })

    // Accounting System
    items.push({
      key: '/accounting',
      icon: <BarChartOutlined />,
      label: 'ระบบบัญชี',
      children: [
        {
          key: '/accounting/chart-of-accounts',
          label: 'ผังบัญชี',
        },
        {
          key: '/accounting/bank-accounts',
          label: 'บัญชีธนาคารบริษัท',
        },
        {
          key: '/accounting/journal-entries',
          label: 'สมุดรายวันทั่วไป',
        },
        { key: '/accounting/transfers', label: 'โอนเงินระหว่างบัญชี' },
        { key: '/accounting/deposits', label: 'ฝากเงินเข้าบัญชี' },
      ]
    })


    if (hasPermission('calendar', 'read')) {
      items.push({
        key: '/calendar',
        icon: <CalendarOutlined />,
        label: 'ปฏิทิน',
      })
    }

    if (hasPermission('notes', 'read')) {
      items.push({
        key: '/notes',
        icon: <FileOutlined />,
        label: 'บันทึก',
      })
    }

    if (hasPermission('reports', 'read')) {
      items.push({
        key: '/reports',
        icon: <BarChartOutlined />,
        label: 'รายงาน',
      })
    }

    // Settings menu
    if (hasPermission('settings', 'read')) {
      items.push({
        key: '/settings',
        icon: <SettingOutlined />,
        label: 'การตั้งค่า',
        children: [
          {
            key: '/settings/company',
            icon: <ShopOutlined />,
            label: 'ข้อมูลบริษัท',
          },
          {
            key: '/users',
            icon: <TeamOutlined />,
            label: 'จัดการผู้ใช้ระบบ',
          },
          {
            key: '/settings/api',
            icon: <ApiOutlined />,
            label: 'การตั้งค่า API',
          },
          {
            key: '/settings/units',
            icon: <AppstoreOutlined />,
            label: 'หน่วยนับ',
          },
          {
            key: '/settings/product-categories',
            icon: <TagOutlined />,
            label: 'หมวดหมู่สินค้า',
          }
        ]
      })

    }


    // OCR Upload is available to all users
    // items.push({
    //   key: '/upload',
    //   icon: <CloudUploadOutlined />,
    //   label: 'อัปโหลดเอกสาร',
    // })

    return items
  }

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'โปรไฟล์',
      onClick: () => setProfileModalVisible(true)
    },
    {
      key: 'change-password',
      icon: <SettingOutlined />,
      label: 'เปลี่ยนรหัสผ่าน',
      onClick: () => setPasswordModalVisible(true)
    },
    {
      type: 'divider'
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ออกจากระบบ',
      onClick: logout
    }
  ]

  const handleMenuClick = (e) => {
    navigate(e.key)
    setMobileDrawerVisible(false)
  }

  const currentPath = location.pathname
  const selectedKeys = [currentPath.split('/').slice(0, 2).join('/')]

  const siderContent = (
    <Menu
      theme="dark"
      mode="inline"
      selectedKeys={selectedKeys}
      items={getMenuItems()}
      onClick={handleMenuClick}
      style={{ height: '100%', borderRight: 0 }}
    />
  )

  return (
    <Layout className="app-layout">
      {/* Desktop Sidebar */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="hidden-mobile"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
      >
        <div className="app-logo" style={{
          height: 64,
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          color: 'white',
          fontSize: collapsed ? '16px' : '18px',
          fontWeight: 600,
          borderBottom: '1px solid #303030'
        }}>
          {collapsed ? 'บจ' : 'ระบบจัดการธุรกิจ'}
        </div>
        {siderContent}
      </Sider>

      {/* Mobile Drawer */}
      <Drawer
        title="เมนู"
        placement="left"
        onClose={() => setMobileDrawerVisible(false)}
        open={mobileDrawerVisible}
        styles={{ padding: 0 }}
        className="visible-mobile"
      >
        {siderContent}
      </Drawer>

      <Layout style={{ marginLeft: window.innerWidth > 768 ? (collapsed ? 80 : 200) : 0 }}>
        {/* Header */}
        <Header className="app-header" style={{
          position: 'fixed',
          zIndex: 1000,
          width: window.innerWidth > 768 ? `calc(100% - ${collapsed ? 80 : 200}px)` : '100%',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Mobile menu button */}
            <Button
              type="text"
              icon={<MenuOutlined />}
              onClick={() => setMobileDrawerVisible(true)}
              className="visible-mobile"
              style={{ color: 'white', marginRight: 16 }}
            />

            {/* Desktop collapse button */}
            <Button
              type="text"
              icon={collapsed ? <MenuOutlined /> : <MenuOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              className="hidden-mobile"
              style={{ color: 'white' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {/* Notifications */}
            <Badge count={0} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                style={{ color: 'white' }}
              />
            </Badge>

            {/* User menu */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background-color 0.3s'
              }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ marginRight: 8 }}
                />
                <span style={{ color: 'white', display: window.innerWidth > 576 ? 'block' : 'none' }}>
                  {user?.name}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>

        {/* Content */}
        <Content
          className="app-content"
          style={{
            marginTop: 64,
            minHeight: 'calc(100vh - 64px)',
            overflow: 'auto'
          }}
        >
          {children}
        </Content>
      </Layout>

      {/* Modals */}
      <ProfileModal
        visible={profileModalVisible}
        onClose={() => setProfileModalVisible(false)}
      />

      <ChangePasswordModal
        visible={passwordModalVisible}
        onClose={() => setPasswordModalVisible(false)}
      />

      {/* Custom styles for responsive design */}
      <style jsx global>{`
        @media (max-width: 768px) {
          .hidden-mobile {
            display: none !important;
          }
          .visible-mobile {
            display: block !important;
          }
        }
        
        @media (min-width: 769px) {
          .visible-mobile {
            display: none !important;
          }
          .hidden-mobile {
            display: block !important;
          }
        }
      `}</style>
    </Layout>
  )
}

export default DashboardLayout